/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import birdata from "../api.js";
import layer from "./generic-tile-layer.js";
import _ from "underscore";
import util from "util/util.js";

import * as utm from "./utm.js";

function tileCornersUtm(tile, proj, forceZoneNum) {
  var scale = Math.pow(2, tile.z) / tile.w;
  return [[0, 0], [0, 1], [1, 1], [1, 0]].map(function(p) {
    var q = proj.fromPointToLatLng(
      new google.maps.Point(
        (tile.x + p[0]) / scale, (tile.y + p[1]) / scale));
    return utm.fromLatLon(q.lat(), q.lng(), forceZoneNum);
  });
}

// Get all zone numbers that a tile might overlap with
function tileZoneNumbers(tile, proj, gridSize, northern) {
  var corners = tileCornersUtm(tile, proj);
  corners[0].easting  -= gridSize;
  corners[0].northing -= gridSize;
  corners[1].easting  -= gridSize;
  corners[1].northing += gridSize;
  corners[2].easting  += gridSize;
  corners[2].northing += gridSize;
  corners[3].easting  += gridSize;
  corners[3].northing -= gridSize;

  return util.uniq(corners.map(p => defaultZone(p.easting, p.northing, p.zoneNum, northern)));
}

function defaultZone(northing, easting, zoneNum, northern) {
  var p = utm.toLatLon(northing, easting, zoneNum, undefined, northern);
  return utm.fromLatLon(p.latitude, p.longitude).zoneNum;
}

function isInDefaultZone(northing, easting, zoneNum, northern) {
  return defaultZone(northing, easting, zoneNum, northern) == zoneNum;
}

function isValidCellCorner(easting, northing, zoneNum, northern, gridSize) {
  return isInDefaultZone(easting + gridSize, northing + gridSize, zoneNum, northern) ||
    isInDefaultZone(easting + gridSize, northing - gridSize, zoneNum, northern) ||
    isInDefaultZone(easting - gridSize, northing + gridSize, zoneNum, northern) ||
    isInDefaultZone(easting - gridSize, northing - gridSize, zoneNum, northern);
}

function linesForTile(tile, proj, options) {
  if(tile.z < options.minZoom) {
    return [];
  }

  var northern = tile.y < Math.pow(2, tile.z - 2);
  var zoneNumbers = tileZoneNumbers(tile, proj, options.gridSize, northern);
  var lines = [];

  _.each(zoneNumbers, function(zoneNum) {
    var points = tileCornersUtm(tile, proj, zoneNum);
    var minx = _.min(points.map(p => Math.floor(p.easting / options.gridSize))) * options.gridSize;
    var maxx = _.max(points.map(p => Math.floor(p.easting / options.gridSize))) * options.gridSize;
    var miny = _.min(points.map(p => Math.floor(p.northing / options.gridSize))) * options.gridSize;
    var maxy = _.max(points.map(p => Math.floor(p.northing / options.gridSize))) * options.gridSize;

    minx -= options.gridSize;
    maxx += options.gridSize;
    miny -= options.gridSize;
    maxy += options.gridSize;

    var x, y, x0, x1, y0, y1, major;
    // Vertical grid lines
    for(x = minx; x <= maxx; x += options.gridSize) {
      major = options.majorGridSize && (x % options.majorGridSize == 0);
      var y0 = miny;
      var y1 = maxy;
      if(isValidCellCorner(x, y0, zoneNum, northern, options.gridSize)) {
        while(!isValidCellCorner(x, y1, zoneNum, northern, options.gridSize)) {
          y1 -= options.gridSize;
        }
      } else {
        if(isValidCellCorner(x, y1, zoneNum, northern, options.gridSize)) {
          while(!isValidCellCorner(x, y0, zoneNum, northern, options.gridSize)) {
            y0 += options.gridSize;
          }
        } else {
          continue;
        }
      }
      lines.push({
        points: [
          utm.toLatLon(x, y0, zoneNum, undefined, northern),
          utm.toLatLon(x, y1, zoneNum, undefined, northern)],
        major: major
      });
    }
    // Horizontal grid lines
    for(y = miny; y <= maxy; y += options.gridSize) {
      major = (y % options.majorGridSize) == 0;
      var x0 = minx;
      var x1 = maxx;
      if(isValidCellCorner(x0, y, zoneNum, northern, options.gridSize)) {
        while(!isValidCellCorner(x1, y, zoneNum, northern, options.gridSize)) {
          x1 -= options.gridSize;
        }
      } else {
        if(isValidCellCorner(x1, y, zoneNum, northern, options.gridSize)) {
          while(!isValidCellCorner(x0, y, zoneNum, northern, options.gridSize)) {
            x0 += options.gridSize;
          }
        } else {
          continue;
        }
      }
      lines.push({
        points: [
          utm.toLatLon(x0, y, zoneNum, undefined, northern),
          utm.toLatLon(x1, y, zoneNum, undefined, northern)],
          major: major
        });
    }
  });

  return lines;
}

function createSVGElement(tag, attrs) {
  var el = document.createElementNS("http://www.w3.org/2000/svg", tag);
  _.each(attrs || {}, function(v, k) {
    el.setAttributeNS(null, k, v);
  });
  return el;
}

function UTMGridMapType(options) {
  this.options = options;
  options.gridSize = options.gridSize || 1000;
  options.majorGridSize = options.majorGridSize || options.gridSize * 10;

  // Google maps requires these;
  this.tileSize = options.tileSize || new google.maps.Size(256, 256);
  this.minZoom = options.minZoom || 12;
  this.maxZoom = options.maxZoom || 20;

  // Used to keep track of how many times each tile has been created and destroyed
  // This is because Google maps sometimes calls getTile() multiple times per tile: (see https://issuetracker.google.com/issues/73335429)
  this.tileCount = {};
}

UTMGridMapType.prototype.getTile = function(coord, zoom, ownerDocument) {
  var key = [coord.x, coord.y, zoom].join(",");

  this.tileCount[key] = this.tileCount[key] ? this.tileCount[key] + 1 : 1;
  if(this.tileCount[key] != 1) {
    var div = document.createElement('div');
    div.setAttribute('data-tile', key);
  }

  var el = createSVGElement("svg", {
    viewBox: [0, 0, this.tileSize.width, this.tileSize.height].join(" "),
    height: this.tileSize.height,
    width: this.tileSize.width,
    'class': 'utm-grid',
    'data-tile': key
  });

  var tile = {
    x: coord.x,
    y: coord.y,
    z: zoom,
    w: this.tileSize.width,
    h: this.tileSize.height
  };

  var map = this.options.map;
  var proj = map.getProjection();

  var lines = linesForTile(tile, proj, this.options);

  _.each(lines, function(line) {
    var pl = line.points.map(p =>
      latLonToTilePixel(p.latitude, p.longitude, proj, tile));
    var strokeWidth = line.major ? 2 : 1;
    el.appendChild(createSVGElement("line", {
      x1: pl[0].x,
      y1: pl[0].y,
      x2: pl[1].x,
      y2: pl[1].y,
      style: "stroke:#333;opacity:0.4;stroke-width:" + strokeWidth
    }));
  });

  return el;
}

function latLonToTilePixel(lat, lon, proj, tile, tileSize) {
  var p = proj.fromLatLngToPoint(new google.maps.LatLng(lat, lon));
  var scale = Math.pow(2, tile.z);
  return {
    x: p.x * scale - tile.x * tile.w,
    y: p.y * scale - tile.y * tile.h
  };
}

UTMGridMapType.prototype.releaseTile = function(node) {
  var key = node.getAttribute('data-tile');

  // Tile book-keeping - see comments on tileCount variable
  this.tileCount[key]--;
  if(this.tileCount[key] == 0) {
    delete this.tileCount[key];
  }
};

export default UTMGridMapType;
